import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { acquireScrollLock, releaseScrollLock } from '@/lib/scrollLock';
import { Icon } from './Icon';
import s from './SidePanel.module.css';
/**
 * Frosted panel over the content area, from the sidebar to the right edge (the
 * whole screen on phones); the page underneath stops scrolling. `narrow` is a
 * form-sized panel on the right with the page still usable beside it. Slides in from
 * the right; closes on ✕ and Esc. Wide, the ✕ floats in the corner over the
 * content; narrower than 920px, it sits in a top bar with the title, so sticky
 * headers inside don't run under it. The children lay out their own padding. Wrap
 * it in <AnimatePresence> for the way out (and <MotionConfig reducedMotion="user">).
 *
 * Portalled to <body>: inside the page it would pick up the page's reveal
 * animation, which overrides the slide.
 */
export function SidePanel({ title, onClose, size = 'full', children, }) {
    const ref = useRef(null);
    // The page underneath doesn't scroll (nor show a second scrollbar) while a
    // full panel covers it; beside a narrow one it stays usable.
    useLayoutEffect(() => {
        if (size !== 'full')
            return;
        acquireScrollLock();
        return releaseScrollLock;
    }, [size]);
    useEffect(() => {
        ref.current?.focus({ preventScroll: true });
    }, []);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return createPortal(_jsxs(motion.aside, { ref: ref, className: s.panel, "data-size": size, role: "dialog", "aria-label": title, tabIndex: -1, initial: { x: '100%' }, animate: { x: 0, transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] } }, exit: { x: '100%', transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } }, children: [_jsxs("div", { className: s.bar, children: [_jsx("div", { className: s.title, children: title }), _jsx("button", { type: "button", className: s.close, onClick: onClose, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", title: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C (Esc)", children: _jsx(Icon, { name: "x", size: 16 }) })] }), _jsx("div", { className: s.body, children: children })] }), document.body);
}
